import { ModuleCommandEnum } from "@/types/enums/module-command.enum";
import { AreaStateAggregateType } from "@/routes/area-aggregate/types/area-state-aggregate.type";
import { useTranslation } from "react-i18next";
// import { useSendCommand } from "@/api/areas/mutations";
import tw from "twin.macro";
import { SheetsType } from "../types/sheets.type";
import { InfoIcon } from "lucide-react";
import { toast } from "sonner";
import { SheetsEnum } from "../enums/sheets.enum";
import StepActionButton from "../step-action-button";
import { StepperContainer } from "../switchs/switch-sheets-header-components";
import Stepper, { StepperStep } from "@/components/Stepper";
import { ArrowDirectionEnum } from "../enums/arrow-directon.enum";
import { useSendCommand } from "@/api/areas/mutations";
import { useAreaDataProvider } from "@/routes/area-aggregate/components/area-data-loader";
import EmergencyStopSupportMessage from "../emergency-stop-support-message";
import { isEmergencyStopActive } from "../utils/is-emergency-stop";

function ActionAreaSheetBody({
  action,
  areaId,
  pushSheet,
}: {
  action: ModuleCommandEnum.UNFOLD | ModuleCommandEnum.FOLD;
  areaId: AreaStateAggregateType["areaId"];
  pushSheet: (view: SheetsType) => void;
}) {
  const { t: translator } = useTranslation();
  const { mutateAsync: sendCommand } = useSendCommand({
    areaId,
  });
  const { applyOptimisticCommand, areaCommandTracking } = useAreaDataProvider();

  // Arrêt d'urgence confirmé : on bloque le lancement d'action et on affiche
  // le message support.
  if (isEmergencyStopActive(areaCommandTracking)) {
    return (
      <div css={[tw`flex flex-col gap-4 px-6 pb-6`]}>
        <EmergencyStopSupportMessage />
      </div>
    );
  }

  const STEPS_CONFIG =
    ModuleCommandEnum.UNFOLD === action
      ? [
          {
            label: translator("action.up"),
            image: "up",
          },
          {
            label: translator("action.open"),
            image: "unfolded",
          },
          {
            label: translator("modulesStatesShort.unfolded"),
            status: "pending",
            image: "valid",
          },
        ]
      : [
          {
            label: translator("action.close"),
            status: "pending",
            image: "up",
          },
          {
            label: translator("action.down"),
            status: "pending",
            image: "folded",
          },
          {
            label: translator("modulesStatesShort.folded"),
            status: "pending",
            image: "valid",
          },
        ];

  return (
    <div css={[tw`flex flex-col gap-4 px-6 pb-6`]}>
      <p css={[tw`text-sm text-gray-500`]}>
        {translator("modulesActions.followSteps")}
      </p>

      <StepperContainer>
        <Stepper
          steps={STEPS_CONFIG.map(
            (step) =>
              ({
                label: step.label,
                status: "pending",
                image: step.image,
              }) as StepperStep
          )}
        />
      </StepperContainer>

      <div css={[tw`flex justify-between border-t border-gray-200 pt-2`]}>
        <div
          css={[
            tw`flex items-center gap-2 border border-gray-200 rounded-md p-2 bg-gray-100`,
          ]}
        >
          <InfoIcon css={[tw`w-6 h-6 text-gray-500`]} />
          <p css={[tw`text-sm font-normal text-gray-500`]}>
            {action === ModuleCommandEnum.UNFOLD
              ? translator("modulesStates.youCanSkipUp")
              : translator("modulesStates.youCanSkipClose")}
          </p>
        </div>
      </div>

      <div tw="pt-10 w-full">
        <StepActionButton
          disabled={false}
          actionTranslation={
            action === ModuleCommandEnum.UNFOLD
              ? translator("areaActions.launchUp")
              : translator("areaActions.launchClose")
          }
          arrowDirection={
            action === ModuleCommandEnum.UNFOLD
              ? ArrowDirectionEnum.UP
              : ArrowDirectionEnum.CLOSE
          }
          onClick={() => {
            try {
              const launchedCommand =
                action === ModuleCommandEnum.UNFOLD
                  ? ModuleCommandEnum.UP
                  : ModuleCommandEnum.CLOSE;
              applyOptimisticCommand(launchedCommand);
              sendCommand({ command: launchedCommand });
              pushSheet({
                id: SheetsEnum.ACTION_IN_PROGRESS,
                context: {
                  currentAction: launchedCommand,
                },
                canGoBack: false,
              });
            } catch (error) {
              toast.error("Erreur lors de la montée");
            }
          }}
        />
        <StepActionButton
          skipStep={true}
          disabled={false}
          actionTranslation={translator("areaActions.skipStep")}
          onClick={() => {
            try {
              pushSheet({
                id: SheetsEnum.SKIP_ACTION,
                canGoBack: true,
                context: {
                  skippedCommand:
                    action === ModuleCommandEnum.UNFOLD
                      ? ModuleCommandEnum.UP
                      : ModuleCommandEnum.CLOSE,
                },
              });
            } catch (error) {
              toast.error("Erreur lors de la montée");
            }
          }}
        />
      </div>
    </div>
  );
}

export default ActionAreaSheetBody;
